/**
 * PersonaGroupPane — the group detail pane: identity + counts, the group's
 * own permission matrix (with the inline narrowing confirm), the
 * inheritance sentence given a real position between the two matrices, and
 * the Members section (existing PersonaRow accordions, showing provenance).
 * Ordered by DESCENDING blast radius per the spec.
 */
import { useState } from "react";
import { useMutation } from "@apollo/client";
import { Info, Plus, Trash2 } from "lucide-react";

import ModuleMatrix from "./ModuleMatrix";
import UpsertModuleModal from "./UpsertModuleModal";
import PersonaRow from "./PersonaRow";
import type { SelectOption } from "./SearchableSelect";
import {
  UPSERT_PERSONA_GROUP_MODULE_MUTATION,
  DEACTIVATE_PERSONA_GROUP_MODULE_MUTATION,
  type AcAdminPersona,
  type AcAdminPersonaModule,
  type AcPersonaGroup,
  type PersonaTemplate,
  type UpsertPersonaGroupModuleVars,
  type DeactivatePersonaGroupModuleVars,
} from "../graphql/adminOps";
import { computeNarrowImpact, type UserCountState } from "../lib/personaGroupLogic";

export default function PersonaGroupPane({
  group,
  members,
  userCountByPersonaCode,
  moduleIdOptions,
  actionCatalog,
  lobOptions,
  templateOptions,
  personaTemplatesByCode,
  onRefresh,
  onDeleteGroup,
  onAddMember,
  onRequestMovePersona,
}: {
  group: AcPersonaGroup;
  members: AcAdminPersona[];
  userCountByPersonaCode: Record<string, UserCountState | undefined>;
  moduleIdOptions: SelectOption[];
  actionCatalog: string[];
  lobOptions: SelectOption[];
  templateOptions: SelectOption[];
  personaTemplatesByCode: Record<string, PersonaTemplate[]>;
  onRefresh: () => void;
  onDeleteGroup: () => void;
  onAddMember: () => void;
  onRequestMovePersona: (persona: AcAdminPersona) => void;
}) {
  const [editTarget, setEditTarget] = useState<{ module?: AcAdminPersonaModule } | null>(null);
  const [pendingNarrow, setPendingNarrow] = useState<{ moduleId: string; action: string | null } | null>(null);

  const [upsertGroupModule] = useMutation<unknown, UpsertPersonaGroupModuleVars>(UPSERT_PERSONA_GROUP_MODULE_MUTATION, {
    onCompleted: onRefresh,
  });
  const [deactivateGroupModule] = useMutation<unknown, DeactivatePersonaGroupModuleVars>(
    DEACTIVATE_PERSONA_GROUP_MODULE_MUTATION,
    { onCompleted: onRefresh },
  );

  // Never collapse a still-loading/failed count into 0 — sum only what's
  // actually known, and say so plainly when the total isn't complete yet.
  const knownUserCounts = members
    .map((p) => userCountByPersonaCode[p.code])
    .filter((c): c is number => typeof c === "number");
  const allUserCountsKnown = knownUserCounts.length === members.length;
  const userCount = knownUserCounts.reduce((n, c) => n + c, 0);
  const verbCount = group.modules.reduce((n, m) => n + (m.actions ?? []).filter((a) => a !== "defaultTab" && a !== "collapsedByDefault").length, 0);
  const nextSortOrder = group.modules.reduce((max, m) => Math.max(max, m.sortOrder), -1) + 1;

  const confirmNarrow = () => {
    if (!pendingNarrow) return;
    const mod = group.modules.find((m) => m.moduleId === pendingNarrow.moduleId);
    if (!mod) {
      setPendingNarrow(null);
      return;
    }
    if (pendingNarrow.action === null) {
      void deactivateGroupModule({ variables: { groupCode: group.code, moduleId: pendingNarrow.moduleId } });
    } else {
      const nextActions = (mod.actions ?? []).filter((a) => a !== pendingNarrow.action);
      void upsertGroupModule({
        variables: {
          groupCode: group.code,
          moduleId: mod.moduleId,
          sortOrder: mod.sortOrder,
          actions: nextActions,
          isActive: mod.isActive,
        },
      });
    }
    setPendingNarrow(null);
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Group identity + counts + Delete group */}
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold">
            {group.displayName} <span className="font-mono font-semibold opacity-55 text-sm ml-2">{group.code}</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Group · <b className="font-semibold text-foreground tabular-nums">{members.length}</b> persona{members.length === 1 ? "" : "s"} ·{" "}
            <b
              className="font-semibold text-foreground tabular-nums"
              title={allUserCountsKnown ? undefined : "Some members' user counts are still loading"}
            >
              {allUserCountsKnown ? userCount : `${userCount}+`}
            </b>{" "}
            user{userCount === 1 && allUserCountsKnown ? "" : "s"} ·{" "}
            <b className="font-semibold text-foreground tabular-nums">{group.modules.length}</b> modules ·{" "}
            <b className="font-semibold text-foreground tabular-nums">{verbCount}</b> action grants
          </p>
        </div>
        <button
          type="button"
          onClick={onDeleteGroup}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-destructive/40 text-destructive text-sm font-medium hover:bg-destructive/10 transition-colors shrink-0"
        >
          <Trash2 className="w-4 h-4" aria-hidden="true" />
          Delete group
        </button>
      </div>

      {/* Group permission matrix */}
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline gap-2.5">
          <h2 className="text-[15px] font-semibold">Group permissions</h2>
          <span className="flex-1" />
          <button
            type="button"
            onClick={() => setEditTarget({})}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-border hover:bg-accent transition-colors"
          >
            <Plus className="w-3.5 h-3.5" aria-hidden="true" />
            Add module
          </button>
        </div>
        {group.modules.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-card p-6 text-center flex flex-col items-center gap-1">
            <h3 className="text-sm font-semibold">No permissions yet</h3>
            <p className="text-xs text-muted-foreground max-w-[52ch]">This group grants nothing, so membership currently has no effect.</p>
          </div>
        ) : (
          <ModuleMatrix
            scope={{ kind: "group", code: group.code, displayName: group.displayName }}
            ownModules={group.modules}
            handlers={{
              upsertModule: (vars) => upsertGroupModule({ variables: { groupCode: group.code, ...vars } }),
              deactivateModule: (moduleId) => deactivateGroupModule({ variables: { groupCode: group.code, moduleId } }),
            }}
            onRefresh={onRefresh}
            onEditModule={(mod) => setEditTarget({ module: mod })}
            narrowing={{
              pending: pendingNarrow,
              impactFor: (moduleId, action) => computeNarrowImpact(members, moduleId, action),
              onRequestRemove: (moduleId, action) => setPendingNarrow({ moduleId, action }),
              onConfirm: confirmNarrow,
              onCancel: () => setPendingNarrow(null),
            }}
          />
        )}
      </div>

      {/* The inheritance sentence — a real position between the two matrices. */}
      <div className="flex gap-2.5 items-start rounded-xl px-3.5 py-2.5 bg-primary/[0.06] border border-primary/20 text-sm">
        <Info className="w-4 h-4 shrink-0 mt-0.5 text-primary" aria-hidden="true" />
        <span>
          <b className="font-semibold">Every persona below inherits these.</b> Members always receive the group&rsquo;s grants and
          cannot drop them locally — they can only add more. Data-scope restrictions can be narrowed by a member, never
          relaxed.
        </span>
      </div>

      {/* Members */}
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline gap-2.5">
          <h2 className="text-[15px] font-semibold">Members</h2>
          <span className="text-xs text-muted-foreground tabular-nums">{members.length}</span>
          <span className="flex-1" />
          <button
            type="button"
            onClick={onAddMember}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-border hover:bg-accent transition-colors"
          >
            <Plus className="w-3.5 h-3.5" aria-hidden="true" />
            Add persona
          </button>
        </div>
        {members.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-card p-6 text-center flex flex-col items-center gap-2">
            <h3 className="text-sm font-semibold">No personas in this group yet</h3>
            <p className="text-xs text-muted-foreground max-w-[52ch]">
              Its {group.modules.length} module permissions aren&rsquo;t reaching anyone. Add a persona, or delete the group.
            </p>
            <button
              type="button"
              onClick={onAddMember}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors mt-1"
            >
              <Plus className="w-4 h-4" aria-hidden="true" />
              Add persona
            </button>
          </div>
        ) : (
          <div>
            {members.map((p) => (
              <PersonaRow
                key={p.code}
                persona={p}
                group={group}
                moduleIdOptions={moduleIdOptions}
                actionCatalog={actionCatalog}
                lobOptions={lobOptions}
                templateOptions={templateOptions}
                personaTemplates={personaTemplatesByCode[p.code] ?? []}
                userCount={userCountByPersonaCode[p.code]}
                showGroupChip={false}
                onRequestMove={() => onRequestMovePersona(p)}
                onRefresh={onRefresh}
              />
            ))}
          </div>
        )}
      </div>

      {editTarget && (
        <UpsertModuleModal
          subjectLabel={`Group: ${group.displayName}`}
          existingModule={editTarget.module}
          nextSortOrder={nextSortOrder}
          moduleIdOptions={moduleIdOptions}
          actionCatalog={actionCatalog}
          lobOptions={lobOptions}
          onClose={() => setEditTarget(null)}
          onSubmit={(vars) => upsertGroupModule({ variables: { groupCode: group.code, ...vars } }).then(onRefresh)}
        />
      )}
    </div>
  );
}
